import type { AssetsAssetSchemaTypedWithId } from '@iconik-io/iconik-api/src/services/assets';
import type {
  FilesFormatSchemaWithId,
  FilesProxySchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';
import type { IconName } from '@iconik-io/uad';

import type { SearchItemEntity } from '../../../../../redux/entities/normalized/search_item';
import { getObjectTypeFromAssetOrSearchItem } from '../../../../../utils/get_object_type_from_asset_or_search_item';
import { getSearchItemMediaType } from '../../../../../utils/get_search_item_media_type';

const mediaTypeIcons: Record<string, IconName> = {
  audio: 'asset_type_audio',
  image: 'asset_type_image',
  subclip: 'asset_type_subclip',
  video: 'asset_type_video',
  pdf: 'Svg2022_uad_metadata',
  link: 'link',
  placeholder: 'asset_type_placeholder',
  collection_isg: 'isg',
  unknown: 'asset_type_unknown',
};

const objectTypeIcons: Record<string, IconName> = {
  assets: mediaTypeIcons.unknown,
  collections: 'collection',
};

function getIconForFileType(
  fileType: string | undefined,
): IconName | undefined {
  if (!fileType) {
    return undefined;
  }
  const [mainType, subType] = fileType.toLowerCase().split('/');
  return (
    mediaTypeIcons[mainType] ??
    mediaTypeIcons[subType] ??
    mediaTypeIcons.unknown
  );
}

export type AssetTypeIconNameOptions = {
  searchItem?: SearchItemEntity | AssetsAssetSchemaTypedWithId | undefined;
  formats?: FilesFormatSchemaWithId[];
  proxies?: FilesProxySchemaWithId[];
  fileType?: string;
};

export function getAssetTypeIconName({
  searchItem,
  fileType,
  proxies = [],
  formats = [],
}: AssetTypeIconNameOptions): IconName | undefined {
  if (!searchItem && !fileType) {
    return undefined;
  }

  if (searchItem?.type === 'SUBCLIP') {
    return mediaTypeIcons.subclip;
  }

  const mediaType = getSearchItemMediaType(searchItem, formats, proxies);
  if (mediaType && mediaType in mediaTypeIcons) {
    return mediaTypeIcons[mediaType];
  }

  const objectType = getObjectTypeFromAssetOrSearchItem(searchItem);
  if (objectType && objectType in objectTypeIcons) {
    return objectTypeIcons[objectType];
  }

  return getIconForFileType(fileType);
}
